// frontend/src/components/AppShell.tsx
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, Sun, Moon, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { NAV } from "../lib/roles";
import { useActionCounts } from "../lib/useActionCounts";
import { Avatar } from "./Avatar";
import { Ticker } from "./Ticker";
import "./logo-shine.css";
import { useQuery } from "@tanstack/react-query";
import { apiList} from "../lib/api";

// const CYCLES = [2025, 2024, 2023];
export function AppShell({ cycle, setCycle, children }: { cycle: number; setCycle: (y: number) => void; children?: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);       // mobile drawer
  const [menu, setMenu] = useState(false);       // profile dropdown
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("nbs-sidebar") === "collapsed");
  const profileRef = useRef<HTMLDivElement>(null);
  

  const CYCLES =  useQuery({ queryKey: ["admin-cycles"], queryFn: () => apiList<any>("/api/cycles/") }).data?.map((c: any) => c.year) ?? [2025, 2024, 2023];

  const groups = NAV
    .filter((g) => g.show(user))
    .map((g) => ({ ...g, items: g.items.filter((it) => (it.show ? it.show(user) : true)) }))
    .filter((g) => g.items.length > 0);
  const counts = useActionCounts();
  useEffect(() => { localStorage.setItem("nbs-sidebar", collapsed ? "collapsed" : "open"); }, [collapsed]);
  useEffect(() => {
    const h = (e: Event) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener("pointerdown", h);
    document.addEventListener("touchstart", h);
    return () => {
      document.removeEventListener("pointerdown", h);
      document.removeEventListener("touchstart", h);
    };
  }, []);
  const signOut = async () => { await logout(); nav("/login"); };
  return (
    <div className={`shell ${collapsed ? "collapsed" : ""}`}>
      <header className="topbar">
        <button className="menu-btn" onClick={() => setOpen((o) => !o)} aria-label="Menu"><Menu size={16} /></button>
        <div className="logos">
          <span className="logo-shine">
            <img src="/nbs-logo.svg" alt="NBS" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
            <img src="/ilo-logo.svg" alt="ILO" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
          </span>
          <span className="sep" />
          <span className="wordmark">Social Protection<small>SDG 1.3.1 statistics</small></span>
        </div>
        <span className="welcome">Welcome,&nbsp;<strong>{user?.first_name || user?.name || "there"}</strong></span>
        <span className="spacer" />
        <NavLink to="/page/instruction" className="header-link" title="Instructions">
          <BookOpen size={15} /> <span className="header-link-label">Instructions</span>
        </NavLink>
        <select value={cycle} onChange={(e) => setCycle(Number(e.target.value))} aria-label="Reporting year">
          {CYCLES.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="icon-btn" onClick={toggle} aria-label="Toggle theme" title={theme === "dark" ? "Light mode" : "Dark mode"}>
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </button>
        <div className="profile" ref={profileRef}>
          <button
            type="button"
            className="profile-toggle"
            aria-haspopup="menu"
            aria-expanded={menu}
            aria-label="Account menu"
            onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }}
          >
            <Avatar name={user?.name || user?.username} />
          </button>
          {menu && (
            <div className="menu" role="menu">
              <div className="head">
                <div className="nm">{user?.name || user?.username}</div>
                {user?.agency && <div className="rl org">{user.agency}</div>}
                {(() => {
                  const roleText = user?.is_superuser ? "Administrator"
                    : (user?.role_label && user.role_label !== "Public / Visitor" ? user.role_label : "");
                  return roleText ? <div className="rl">{roleText}</div> : null;
                })()}
                {user?.email && <div className="rl">{user.email}</div>}
              </div>
              <button className="item" onClick={signOut}><LogOut size={15} /> Sign out</button>
            </div>
          )}
        </div>
      </header>
      <div className="ticker-bar"><Ticker cycle={cycle} /></div>
      <nav className={`sidebar ${open ? "drawer-open" : ""}`}>
        <div onClick={() => setOpen(false)}>
          {groups.map((g) => (
            <div key={g.group}>
              <div className="group">{g.group}</div>
              {g.items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink key={it.to} to={it.to} end={["/", "/agency", "/nbs"].includes(it.to)}
                    className={({ isActive }) => (isActive ? "active" : "")}>
                    <Icon className="ico" size={17} />
                    <span className="label">{it.label}</span>
                    {counts[it.to] > 0 && <span className="nav-badge">{counts[it.to]}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </div>
        <button className="collapse-btn" onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span className="label">Collapse</span></>}
        </button>
      </nav>
      <main className="main">{children ?? <Outlet />}</main>
    </div>
  );
}
